import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'

const Context = createContext()

const DEFAULT_GEO_OPTS = {
    enableHighAccuracy: true, // request GPS/high-accuracy providers
    timeout: 15000, // fail if no fix within 15s
    maximumAge: 0, // do not use a cached position
}

export function useUserLocation() {
    if (!Context)
        throw new Error('useUserLocation must be used within UserLocationProvider')
    return useContext(Context)
}

export function UserLocationProvider({ children }) {
    const [userLocation, setUserLocation] = useState({
        loaded: false,
        coordinates: null,
        accuracy: null,
        permission: 'prompt',
        error: null,
    })
    const watchIdRef = useRef(null)

    const getCurrentPosition = useCallback((opts = {}) => {
        return new Promise((resolve, reject) => {
            if (!('geolocation' in navigator))
                return reject(new Error('Geolocation not supported'))
            const options = { ...DEFAULT_GEO_OPTS, ...opts }
            console.log('Getting one shot position')
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const next = {
                        loaded: true,
                        coordinates: {
                            lat: pos.coords.latitude,
                            lng: pos.coords.longitude,
                        },
                        accuracy: pos.coords.accuracy,
                        permission: 'granted',
                        error: null,
                    }
                    setUserLocation(next)
                    resolve(next)
                },
                (err) => {
                    setUserLocation((prev) => ({
                        ...prev,
                        error: err.message,
                        permission: 'denied',
                    }))
                    reject(err)
                },
                options
            )
        })
    }, [])

    const watchLocation = useCallback((opts = {}) => {
        if (!('geolocation' in navigator)) return null
        const options = { ...DEFAULT_GEO_OPTS, ...opts }

        // restart watch with new options
        if (watchIdRef.current != null) {
            navigator.geolocation.clearWatch(watchIdRef.current)
            watchIdRef.current = null
        }

        const id = navigator.geolocation.watchPosition(
            (pos) => {
                console.log('Watch position update')
                setUserLocation({
                    loaded: true,
                    coordinates: { lat: pos.coords.latitude, lng: pos.coords.longitude },
                    accuracy: pos.coords.accuracy,
                    permission: 'granted',
                    error: null,
                })
            },
            (err) => {
                setUserLocation((prev) => ({
                    ...prev,
                    error: err.message,
                    permission: 'denied',
                }))
            },
            options
        )
        watchIdRef.current = id
        return id
    }, [])

    const clearWatch = useCallback(() => {
        if (watchIdRef.current != null) {
            navigator.geolocation.clearWatch(watchIdRef.current)
            console.log("No longer watching user's position")
            watchIdRef.current = null
        }
    }, [])

    // Optional: detect permission state (best-effort)
    useEffect(() => {
        let cancelled = false
        if (navigator.permissions?.query) {
            navigator.permissions
                .query({ name: 'geolocation' })
                .then((res) => {
                    if (!cancelled) {
                        setUserLocation((prev) => ({ ...prev, permission: res.state }))
                        if (res.state === 'granted') {
                            getCurrentPosition()
                        }
                        res.onchange = () =>
                            setUserLocation((prev) => ({
                                ...prev,
                                permission: res.state,
                            }))
                    }
                })
                .catch(() => {})
        }
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line
    }, [])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (watchIdRef.current != null)
                navigator.geolocation.clearWatch(watchIdRef.current)
        }
    }, [])

    const value = {
        userLocation,
        getCurrentPosition, // one-shot with high accuracy by default
        watchLocation, // continuous updates with high accuracy by default
        clearWatch,
    }

    return <Context.Provider value={value}>{children}</Context.Provider>
}
